import { FieldProps, useField } from 'informed';
import moment, { Moment } from 'moment';
import Picker from 'rc-picker';
import { PanelMode } from 'rc-picker/es/interface';
import { PickerProps } from 'rc-picker/es/Picker';
import momentGenerateConfig from 'rc-picker/lib/generate/moment';
import csCZ from 'rc-picker/lib/locale/cs_CZ';
import { useCallback, useEffect } from 'react';

import { InputWrapper, InputWrapperProps } from '../common';
import { getHolidaysOnDate, HolidayWithDate, isDayHoliday } from '@tymbe/utils/holiday';
import { BadgePill } from '@tymbe/ty-components/indicators';
import { ActionIcon, ContentIcon, CustomIcon } from '@tymbe/ui-icons';

type Props = Omit<Partial<PickerProps<Moment>>, 'disabledTime' | 'picker' | 'allowClear' | 'defaultValue'> & InputWrapperProps & {
  label?: string;
  isClearable?: boolean;
  datePresets?: { label: string, date: Array<string | Date | Moment> }[];
  highlightHolidays?: boolean;
  hidden?: boolean;
};

export const TyDateMultiPicker = <Fields extends object>(props: FieldProps<Props, Moment[], Fields>) => {
  const {
    render,
    fieldState,
    userProps,
    fieldApi,
    ref,
  } = useField(props);

  const { error, showError, focused, value } = fieldState;
  const { setValue, setTouched, setFocused } = fieldApi;
  const {
    id,
    label,
    className = '',
    generateConfig = momentGenerateConfig,
    locale = csCZ,
    open = focused,
    onChange,
    onFocus,
    onBlur,
    suffixIcon, // = <CalendarIcon />,
    datePresets = [],
    renderExtraFooter,
    isClearable = false,
    highlightHolidays = false,
    simple,
    icon,
    suffix,
    description,
    hidden,
    ...rest
  } = userProps;

  useEffect(() => {
    ref.current?.setCustomValidity(error || '');
  }, [error, ref]);

  // Picker uses legacy ref at least it's typed as LegacyRef
  const pickerRef = { current: null } as { current: Picker<Moment> | null };

  const innerOnChange = (date: Moment) => {
    // do we need remove already picked date
    const remove = value?.find((val) => date.isSame(val, 'day'));

    const newValues = [...(value || [])];

    if (remove) {
      newValues.splice(value.indexOf(remove), 1);
    } else {
      newValues.push(date);
    }

    setValue(newValues.length ? newValues : undefined);
  };
  const innerOnFocus: PickerProps<Moment>['onFocus'] = (e) => {
    setFocused(true, e);
  };
  const innerOnBlur: PickerProps<Moment>['onBlur'] = (e) => {
    setTouched(true, e);
    setFocused(false, e);
  };

  const innerRenderExtraFooter: PickerProps<Moment>['renderExtraFooter'] = useCallback((mode: PanelMode) => (
    <>
      {datePresets.map(
        (datePreset) => (
          <button
            key={datePreset.label}
            type="button"
            onClick={() => setValue(datePreset.date.map((date) => moment(date)))}
          >
            {datePreset.label}
          </button>
        ),
      )}
      {renderExtraFooter?.(mode) ?? null}
    </>
  ), [datePresets, renderExtraFooter, setValue]);

  const holidays = value && highlightHolidays
    ? value.map((date: Moment) => getHolidaysOnDate(date)).flat()
    : [];

  const focusedClassName = focused ? 'ty_input_field__focused' : '';
  const filledClassName = value?.length ? 'ty_input_field__filled' : '';

  const dateRender = (date: Moment): React.ReactNode => {
    const dayIsHoliday = highlightHolidays && isDayHoliday(date);

    return (
      <div
        className={`rc-picker-cell-inner
          ${value?.some((d) => d.isSame(date, 'day')) ? 'selected' : ''}
          ${dayIsHoliday && '!text-danger !bg-danger-150 !border-danger'}
        `}
      >
        {date.date()}
      </div>
    );
  };

  return render(
    <InputWrapper
      className={className}
      simple={simple}
      icon={icon}
      suffix={suffix}
      label={label}
      error={showError ? String(error) : undefined}
      description={description}
      hidden={hidden}
    >
      <div
        role="none"
        className="flex flex-wrap gap-2"
        onClick={(e) => {
          if (e.target !== e.currentTarget || !pickerRef.current) return;
          pickerRef.current.focus();
        }}
      >
        {value?.sort((a, b) => a.diff(b, 'day')).map((date) => (
          <BadgePill variant="default" className="flex gap-2">
            {date.format('L')}
            <ContentIcon name="clear" className="size-4 cursor-pointer" onClick={(e) => {
              e.preventDefault();
              innerOnChange(date);
            }} />
          </BadgePill>
          // <div className="ty_select__multi-value" key={date.toISOString()}>
          //   <div className={`ty_select__multi-value__label ${isDayHoliday(date) ? 'ty_select__multi-value__label-warning' : ''}`}>
          //     {date.format('L')}
          //   </div>
          //   <div className="ty_select__multi-value__remove" role="none" onClick={() => innerOnChange(date)}>
          //     #
          //   </div>
          // </div>
        ))}
      </div>
        <Picker<Moment>
          {...rest}
          ref={(tmp) => {
            pickerRef.current = tmp;
          }}
          pickerRef={ref}
          defaultValue={undefined}
          allowClear={isClearable}
          className="w-4"
          dropdownClassName="[&_.rc-picker-cell]:p-0 [&_.rc-picker-cell]:!size-[35px] [&_.selected]:!bg-primary-400 [&_.selected]:text-white"
          id={id}
          format={() => ''}
          generateConfig={generateConfig}
          picker="date"
          locale={locale}
          value={null}
          open={open}
          onSelect={innerOnChange}
          onFocus={innerOnFocus}
          onBlur={innerOnBlur}
          suffixIcon={(
            <>
              {!value?.length ? null : (
                <div
                  role="none"
                  className="clear-icon pr-3"
                  onClick={() => setValue(undefined)}
                >
                  X
                </div>
              )}
              {suffixIcon}
            </>
          )}
          renderExtraFooter={innerRenderExtraFooter}
          dateRender={dateRender}
        />
      <input hidden ref={ref} className="hidden" />
      {holidays.length !== 0 && (
        <div className="text-danger">
          V některý z vybraných dní je státní svátek ({holidays.map((holiday: HolidayWithDate) => `${holiday.date.format('DD.MM.YYYY')} - ${holiday.name}`).join(', ')}).
        </div>
      )}
    </InputWrapper>,
  );
};
